import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import './CompactPortraitView.css';
import CompactEditDialog from './CompactEditDialog';
import CompactAnalysisDialog from './CompactAnalysisDialog';

const CompactPortraitView = ({
  // Zeit-States (startTime = bei laufender Uhr für Live-Anzeige ohne App-Re-Render)
  currentTime = 0,
  startTime = null,
  isRunning = false,
  measurements = [],
  lastMeasurementTime = 0,
  formatTime,
  formatSollzeit = null,
  decimalPlacesSollzeiten = 4,
  
  // Handlers
  onStart,
  onStop,
  onReset,
  
  // AblaufDB (showAblaufDb = durchgängig wie Normal-Ansicht: Aus → Listen aus, alles von Hand)
  showAblaufDb = true,
  ablaufDbData = [],
  einflussDbData = [],
  dependentListsData = [],
  selectedKuerzel = null,
  onKuerzelSelect,
  
  // Einflussgrößen
  einflussgroessenValues = {},
  onEinflussgroesseChange,
  
  // Leistungsgrade (mitLeistungsgrade = false → nur Button „übernehmen“ (0) über Restbreite)
  mitLeistungsgrade = true,
  grades = [0, 80, 90, 100, 105, 110, 115, 120, 125],
  onGradeClick,
  isInterrupted = false,
  onInterruptionToggle,
  interruptionMeasurementNumber = null,
  
  // Messungen
  onEditMeasurement,
  onDeleteMeasurement,
  onViewPhotos,
  onViewNote,
  measurementPhotos = {}, // { measurementNumber: [photos] }
  onAddPhoto,
  onRemovePhoto,
  onCopyActivity,
  
  // Mini-Buttons
  onVoiceClick,
  onNoteClick,
  onPhotoClick,
  pendingPhotosCount = 0,
  isListening = false,
  
  // Session
  selectedSession = null,
  resumeNumber = 1,
  
  // Activity und Note (onActivityEnter = Enter im Tätigkeitsfeld, durchgängig „wie“+Zahl + Kürzel + Messung ohne LG)
  onActivityEnter = null,
  activity = '',
  setActivity,
  note = '',
  setNote,
  
  // Zeitarten für Edit-Dialog
  timeTypes = [],
  timeType = '',
  setTimeType = null,
  
  // Dark Mode
  darkMode = false,
  
  // Input-Section anzeigen
  showInputs = false,
  
  // Foto- und Sprach-Handler für Edit-Dialog
  onPhotoUpload = null,
  onPhotoDelete = null,
  loadPhotos = null,
  
  // Auswertung
  licenseStatus = false,
  getAnalysis = null,
  showAnalysis = false,
  onCloseAnalysis = null,
  pivotDrillDown = null,
  setPivotDrillDown = null,
  
  // Backend-Status
  backendAvailable = null,
  backendUrl = null,
  
  // M1 Reset Handler
  onResetMerke1 = null,
  
  // Speech Recognition Support
  isSpeechRecognitionSupported = false,
  
  // Haptic Feedback
  hapticFeedbackEnabled = true
}) => {
  const { t } = useTranslation();
  const [selectedEinflussgroesse, setSelectedEinflussgroesse] = useState(null);
  const [lastGrade, setLastGrade] = useState(null);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null); // Für Lightbox
  const [photoPreviewIndex, setPhotoPreviewIndex] = useState(0); // Aktueller Index in der Foto-Galerie
  const [photoPreviewList, setPhotoPreviewList] = useState([]); // Liste aller Fotos für Galerie
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [ablaufContainerHeight, setAblaufContainerHeight] = useState(280); // Start-Höhe (reduziert um 1 Item)
  const [isResizing, setIsResizing] = useState(false);
  const [showEinflussgroessen, setShowEinflussgroessen] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_show_einflussgroessen_compact_view');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  const [showNotizen, setShowNotizen] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_show_notizen_compact_view');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  // Live-Zeit bei laufender Uhr (eigener State, kein App-Re-Render) – verhindert Cursor-Flackern
  const [displayTime, setDisplayTime] = useState(0);
  const normalizeMesswerte = useCallback((values) => {
    if (!Array.isArray(values)) return [];
    return values
      .map((mw) => (typeof mw === 'string' ? mw : (mw?.messwert || '')))
      .map((v) => (v || '').toString().trim())
      .filter((v) => v);
  }, []);

  const getDependentOptionValues = useCallback((einfluss) => {
    if (!einfluss || !einfluss.einflussname || !selectedKuerzel?.einflussgroessen) return [];
    const targetName = (einfluss.einflussname || '').toString().trim();
    const sheetName = (einfluss.einheit || '').toString().trim();
    if (!targetName || !sheetName) return [];
    const sheet = (dependentListsData || []).find((s) => (s?.name || '').trim() === sheetName);
    if (!sheet || !Array.isArray(sheet.columns) || !Array.isArray(sheet.rows) || sheet.columns.length === 0) return [];

    const targetIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === targetName);
    if (targetIdx < 0) return [];

    const fields = (selectedKuerzel.einflussgroessen || [])
      .filter((f) => {
        const typ = ((f?.typ || f?.klassewert || '').toString().trim().charAt(0) || '');
        const fSheet = (f?.einheit || '').toString().trim();
        return (typ === 'l' || typ === 'L') && fSheet === sheetName;
      })
      .map((f) => (f?.einflussname || '').toString().trim())
      .filter((n) => n);

    const rows = (sheet.rows || []).filter((row) => {
      if (!Array.isArray(row)) return false;
      return fields.every((fieldName) => {
        if (fieldName === targetName) return true;
        const selected = (einflussgroessenValues?.[fieldName] || '').toString().trim();
        if (!selected) return true;
        const colIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === fieldName);
        if (colIdx < 0) return true;
        return ((row[colIdx] || '').toString().trim() === selected);
      });
    });

    return Array.from(new Set(rows.map((row) => (row[targetIdx] || '').toString().trim()).filter((v) => v)));
  }, [dependentListsData, einflussgroessenValues, selectedKuerzel]);

  const getEinflussOptionValues = useCallback((einfluss) => {
    if (!einfluss || !einfluss.einflussname) return [];
    const name = einfluss.einflussname.trim();
    if (!name) return [];
    const typRaw = (einfluss.typ || einfluss.klassewert || '').toString().trim();
    const typ = typRaw ? typRaw.charAt(0) : '';
    const isDependentListType = typ === 'l' || typ === 'L';
    const isEinflussDbType = typ === 'x' || typ === 'X';

    if (isDependentListType) {
      const dependentOptions = getDependentOptionValues(einfluss);
      if (dependentOptions.length > 0) {
        return dependentOptions;
      }
      return normalizeMesswerte(einfluss.messwerte);
    }

    if (isEinflussDbType) {
      const dbEntry = (einflussDbData || []).find((item) => (item?.name || '').trim() === name);
      if (dbEntry?.messwerte) {
        return normalizeMesswerte(dbEntry.messwerte);
      }
      return normalizeMesswerte(einfluss.messwerte);
    }

    return normalizeMesswerte(einfluss.messwerte);
  }, [einflussDbData, getDependentOptionValues, normalizeMesswerte]);
  useEffect(() => {
    if (!isRunning || !startTime) {
      setDisplayTime(0);
      return undefined;
    }
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setDisplayTime(Math.round((elapsed / 1000 / 60) * 100));
    }, 100);
    return () => clearInterval(interval);
  }, [isRunning, startTime]);
  const effectiveTime = isRunning && startTime ? displayTime : currentTime;
  const isResizingRef = useRef(false);
  const resizerRef = useRef(null);
  const resizeStartYRef = useRef(0);
  const resizeStartHeightRef = useRef(312.5);
  const animationFrameRef = useRef(null);
  const containerRef = useRef(null);
  
  // Refs für Virtualisierung
  const measurementsListRef = useRef(null);
  const parentRef = useRef(null);
  
  // Refs für Swipe-Gesten
  const swipeStartX = useRef({});
  
  // Ref für Eingabefeld (Ziffernblock)
  const auspraegungInputRef = useRef(null);
  const ziffernblockClickRef = useRef(false); // Track ob Klick auf Ziffernblock-Button war
  
  // Refs für Click-Detection (wie in Normalansicht)
  const lastClickTimeRef = useRef(0);
  const clickTimeoutRef = useRef(null);

  // Speichere EA-Anzeige-Einstellung in localStorage
  useEffect(() => {
    localStorage.setItem('stoppuhr_show_einflussgroessen_compact_view', showEinflussgroessen.toString());
  }, [showEinflussgroessen]);

  useEffect(() => {
    localStorage.setItem('stoppuhr_show_notizen_compact_view', showNotizen.toString());
  }, [showNotizen]);

  // Berechne letzten Leistungsgrad
  useEffect(() => {
    if (measurements.length > 0) {
      const lastMeasurement = measurements[measurements.length - 1];
      setLastGrade(lastMeasurement.grade || null);
    } else {
      setLastGrade(null);
    }
  }, [measurements]);

  // Track Scroll-Position für Navigation-Buttons und speichere sie
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    let scrollTimeout = null;
    
    const updateScrollState = () => {
      const scrollTop = scrollElement.scrollTop;
      const scrollHeight = scrollElement.scrollHeight;
      const clientHeight = scrollElement.clientHeight;
      
      setCanScrollUp(scrollTop > 0);
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1);
      
      // Speichere Scroll-Position in localStorage (pro Session)
      // Verwende Debouncing, um nicht bei jedem Scroll-Event zu speichern
      if (selectedSession?.id) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const storageKey = `stoppuhr_session_${selectedSession.id}_portrait_scroll`;
          try {
            localStorage.setItem(storageKey, scrollTop.toString());
          } catch (error) {
            console.error('[Portrait] Fehler beim Speichern der Scroll-Position:', error);
          }
        }, 100); // Speichere 100ms nach dem letzten Scroll-Event
      }
    };

    updateScrollState();
    scrollElement.addEventListener('scroll', updateScrollState);
    
    // Auch bei Größenänderung prüfen
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(scrollElement);
    
    // Speichere auch beim Unmount
    const saveOnUnmount = () => {
      if (selectedSession?.id && scrollElement) {
        const storageKey = `stoppuhr_session_${selectedSession.id}_portrait_scroll`;
        try {
          localStorage.setItem(storageKey, scrollElement.scrollTop.toString());
        } catch (error) {
          console.error('[Portrait] Fehler beim Speichern der Scroll-Position (unmount):', error);
        }
      }
    };

    return () => {
      clearTimeout(scrollTimeout);
      scrollElement.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
      saveOnUnmount(); // Speichere beim Unmount
    };
  }, [measurements.length, selectedSession?.id]);

  // Ref um zu tracken, ob Scroll-Position bereits wiederhergestellt wurde
  const scrollRestoredRef = useRef(false);
  const lastSessionIdRef = useRef(null);
  const restoreTimeoutRef = useRef(null);

  // Stelle Scroll-Position beim Laden wieder her
  // Nur einmal pro Session, nicht bei jeder Messungsänderung
  useEffect(() => {
    // Reset wenn Session wechselt
    if (lastSessionIdRef.current !== selectedSession?.id) {
      scrollRestoredRef.current = false;
      lastSessionIdRef.current = selectedSession?.id;
      // Lösche eventuell laufenden Timeout
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }
    }

    // Nur wiederherstellen, wenn noch nicht geschehen und Session vorhanden
    if (!scrollRestoredRef.current && selectedSession?.id && parentRef.current && measurements.length > 0) {
      const storageKey = `stoppuhr_session_${selectedSession.id}_portrait_scroll`;
      try {
        const savedScroll = localStorage.getItem(storageKey);
        if (savedScroll) {
          const scrollTop = parseInt(savedScroll, 10);
          if (!isNaN(scrollTop) && scrollTop >= 0) {
            // Warte bis Virtualisierung bereit ist
            // Verwende einen intelligenten Ansatz: Warte auf stabile scrollHeight
            const restoreScroll = () => {
              if (parentRef.current && !scrollRestoredRef.current) {
                const currentScrollHeight = parentRef.current.scrollHeight;
                const currentClientHeight = parentRef.current.clientHeight;
                const currentScrollTop = parentRef.current.scrollTop;
                
                // Nur wiederherstellen, wenn die Liste genug Inhalt hat
                if (currentScrollHeight > currentClientHeight) {
                  // Berechne maximale Scroll-Position
                  const maxScrollTop = currentScrollHeight - currentClientHeight;
                  // Begrenze die gespeicherte Position auf den gültigen Bereich
                  const validScrollTop = Math.min(Math.max(0, scrollTop), maxScrollTop);
                  
                  // Prüfe, ob die Scroll-Position noch nicht gesetzt wurde (Toleranz: 5px)
                  if (Math.abs(currentScrollTop - validScrollTop) > 5) {
                    // Setze Scroll-Position nur einmal, ohne Animation
                    parentRef.current.scrollTop = validScrollTop;
                    scrollRestoredRef.current = true;
                    console.log('[Portrait] Scroll-Position wiederhergestellt:', validScrollTop, '(max:', maxScrollTop, ')');
                  } else {
                    // Bereits korrekt gesetzt
                    scrollRestoredRef.current = true;
                  }
                } else {
                  // Liste noch nicht groß genug, versuche es später nochmal
                  if (!restoreTimeoutRef.current) {
                    restoreTimeoutRef.current = setTimeout(() => {
                      restoreTimeoutRef.current = null;
                      if (!scrollRestoredRef.current) {
                        restoreScroll();
                      }
                    }, 100);
                  }
                }
              }
            };
            
            // Versuche nach mehreren Frames, damit Virtualisierung fertig ist
            // Doppeltes requestAnimationFrame für bessere Timing-Kontrolle
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                restoreScroll();
              });
            });
          } else {
            // Keine gespeicherte Position oder Position ist 0
            scrollRestoredRef.current = true;
          }
        } else {
          // Keine gespeicherte Position vorhanden
          scrollRestoredRef.current = true;
        }
      } catch (error) {
        console.error('[Portrait] Fehler beim Laden der Scroll-Position:', error);
        scrollRestoredRef.current = true; // Markiere als versucht, auch bei Fehler
      }
    } else if (measurements.length === 0) {
      // Keine Messungen vorhanden, markiere als wiederhergestellt
      scrollRestoredRef.current = true;
    }
    
    // Cleanup
    return () => {
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }
    };
  }, [selectedSession?.id, measurements.length]);

  // Resizer-Handler für Ablauf-Container
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const listsContainer = resizerRef.current?.previousElementSibling;
    if (!listsContainer) {
      return;
    }
    
    // Speichere Startposition und Start-Höhe
    const mouseY = e.clientY || (e.touches && e.touches[0]?.clientY);
    if (mouseY !== undefined) {
      resizeStartYRef.current = mouseY;
      
      // WICHTIG: Lese Höhe in dieser Reihenfolge:
      // 1. inline-style (hat Vorrang, wird von React gesetzt)
      // 2. offsetHeight (tatsächliche gerenderte Höhe)
      // 3. computed style
      // 4. State als Fallback
      const inlineHeight = listsContainer.style.height;
      const minAblaufHeight = 200;
      let currentHeight = inlineHeight 
        ? parseInt(inlineHeight, 10)
        : (listsContainer.offsetHeight || 
           parseInt(window.getComputedStyle(listsContainer).height, 10) || 
           ablaufContainerHeight);
      
      // FIX: Füge resizing-Klasse ZUERST hinzu (deaktiviert CSS-Transitions)
      // Verhindert visuellen Sprung beim Setzen der Höhe
      listsContainer.classList.add('resizing');
      
      // FIX: Wenn Start-Höhe unter Minimum liegt, setze sie auf Minimum
      // Verhindert Sprung beim ersten kleinen Zug
      if (currentHeight < minAblaufHeight) {
        currentHeight = minAblaufHeight;
        // Setze Höhe im DOM, damit sie korrekt ist
        // Transition ist bereits deaktiviert durch resizing-Klasse
        listsContainer.style.height = `${minAblaufHeight}px`;
      }
      
      resizeStartHeightRef.current = currentHeight;
      
      isResizingRef.current = true;
      setIsResizing(true);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    }
  }, [ablaufContainerHeight]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizingRef.current || !containerRef.current) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const listsContainer = resizerRef.current?.previousElementSibling;
    if (!listsContainer) {
      return;
    }
    
    const mouseY = e.clientY || (e.touches && e.touches[0]?.clientY);
    if (mouseY === undefined) {
      return;
    }
    
    // Berechne Änderung relativ zur Startposition
    const deltaY = mouseY - resizeStartYRef.current;
    const newHeight = resizeStartHeightRef.current + deltaY;
    
    // Mindesthöhen:
    // Ablauf: 4 Items * 40px + Header 40px = 200px (um 2 Items reduziert)
    // Tätigkeiten: 2 Items * 60px + Header 50px = 170px (wird durch CSS min-height sichergestellt)
    const minAblaufHeight = 200;
    
    // Maximale Höhe: ca. 70% der Viewport-Höhe (Flexbox sorgt für Rest)
    const maxAblaufHeight = window.innerHeight * 0.7;
    
    // Begrenze die neue Höhe auf Min/Max
    const clampedHeight = Math.max(minAblaufHeight, Math.min(maxAblaufHeight, newHeight));
    
    // Verwende requestAnimationFrame für smooth Updates
    // Storniere vorherige Animation-Frame, falls vorhanden
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      // Direkte DOM-Manipulation während Resizing (schneller als State-Updates)
      if (listsContainer && isResizingRef.current) {
        listsContainer.style.height = `${clampedHeight}px`;
      }
    });
  }, []);

  const handleResizeEnd = useCallback(() => {
    // Storniere pending Animation-Frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    const listsContainer = resizerRef.current?.previousElementSibling;
    
    // Entferne resizing-Klasse (aktiviert CSS-Transitions wieder)
    if (listsContainer) {
      listsContainer.classList.remove('resizing');
      
      // Lese finale Höhe aus inline-style (hat Vorrang) oder offsetHeight
      const inlineHeight = listsContainer.style.height;
      const finalHeight = inlineHeight 
        ? parseInt(inlineHeight, 10)
        : listsContainer.offsetHeight;
      
      if (!isNaN(finalHeight) && finalHeight > 0) {
        setAblaufContainerHeight(finalHeight);
        // Entferne inline-style, damit React neues inline-style basierend auf State setzt
        listsContainer.style.height = '';
      }
    }
    
    isResizingRef.current = false;
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Event-Listener für Resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingRef.current) {
        handleResizeMove(e);
      }
    };
    
    const handleMouseUp = () => {
      if (isResizingRef.current) {
        handleResizeEnd();
      }
    };
    
    const handleTouchMove = (e) => {
      if (isResizingRef.current) {
        handleResizeMove(e);
      }
    };
    
    const handleTouchEnd = () => {
      if (isResizingRef.current) {
        handleResizeEnd();
      }
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        // Cleanup: Storniere pending Animation-Frame
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);



  // Berechne Einzelzeit (nutzt effectiveTime = Live-Anzeige oder currentTime)
  const getEinzelzeit = () => {
    if (!isRunning) return 0;
    if (measurements.length === 0) {
      return Math.round(effectiveTime);
    }
    return Math.max(0, Math.round(effectiveTime - lastMeasurementTime));
  };

  const einzelzeit = getEinzelzeit();

  // Handler für Click mit Double-Click-Detection
  const handleMeasurementClick = (measurement, event) => {
    // Verhindere Button-Klicks
    if (event.target.closest('.action-btn')) return;
    
    // Wenn auf einen alten Messpunkt geklickt wird, setze M1 zurück
    if (onResetMerke1) {
      onResetMerke1();
    }
    
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    if (timeSinceLastClick < 300) {
      // Double click detected - öffne Edit-Dialog
      clearTimeout(clickTimeoutRef.current);
      setEditingMeasurement(measurement);
    } else {
      // Single click - wait to see if it's a double click
      clickTimeoutRef.current = setTimeout(() => {
        if (onCopyActivity) {
          onCopyActivity(measurement);
        }
      }, 300);
    }
    
    lastClickTimeRef.current = now;
  };

  // Cleanup für Timeout beim Unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  // Handler für Einflussgrößen-Selektion
  const handleEinflussgroesseSelect = (einflussname) => {
    if (selectedEinflussgroesse === einflussname) {
      setSelectedEinflussgroesse(null);
    } else {
      setSelectedEinflussgroesse(einflussname);
    }
  };

  // handleAuspraegungSelect und getAvailableAuspraegungen wurden entfernt (unbenutzt).

  // Handler für Kürzel-Selektion
  const handleKuerzelClick = (item) => {
    // Wenn das Kürzel bereits selektiert ist, setze nur die Einflussgrößen-Selektion zurück
    if (selectedKuerzel?.kuerzel === item.kuerzel) {
      setSelectedEinflussgroesse(null); // Reset nur die Selektion, behalte Werte
      return;
    }
    
    // Neues Kürzel: Normaler Flow
    setSelectedEinflussgroesse(null); // Reset bei neuer Kürzel-Selektion
    onKuerzelSelect(item);
  };

  // Haptic Feedback für Leistungsgrade
  const triggerHaptic = (type = 'light') => {
    if (!hapticFeedbackEnabled) return;
    
    // Prüfe ob iPad (kein Vibrationsmotor)
    const isIPad = /iPad/.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIPad) {
      // Akustisches Feedback für iPad
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800; // Tonhöhe
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } catch (e) {
        console.debug('[Haptic Feedback] Audio-Feedback nicht verfügbar:', e);
      }
    } else {
      // Vibration für iPhone und andere Geräte
      if (navigator.vibrate) {
        try {
          navigator.vibrate(100); // 100ms Vibration
        } catch (e) {
          console.debug('[Haptic Feedback] Vibration nicht verfügbar:', e);
        }
      }
    }
  };

  // Handler für Leistungsgrad mit Haptic Feedback
  const handleGradeClickWithHaptic = (grade) => {
    triggerHaptic('light');
    onGradeClick(grade);
  };

  // Prüfe ob Messung leer ist (keine Zeit, kein Leistungsgrad)
  const isMeasurementEmpty = (measurement) => {
    return !measurement.grade && (!measurement.individualTime || measurement.individualTime === 0);
  };

  // Virtualisierung für Tätigkeiten-Liste (umgekehrt sortiert - neueste zuerst)
  const reversedMeasurements = [...measurements].reverse();
  const virtualizer = useVirtualizer({
    count: reversedMeasurements.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Geschätzte Höhe pro Item
    overscan: 5
  });

  return (
    <div 
      ref={containerRef}
      className={`compact-portrait-view ${darkMode ? 'dark-mode' : ''}`}
    >
      {/* Zeit-Section */}
      <div className="compact-time-section">
        <div className="compact-time-display">
          <span className="compact-fz">
            FZ: {formatTime(effectiveTime)}
            {resumeNumber >= 1 && <span className="compact-wa"> WA{resumeNumber}</span>}
          </span>
          <span className="compact-ez">
            EZ: {formatTime(einzelzeit)}
            {lastGrade && ` (${lastGrade})`}
          </span>
        </div>
        <div className="compact-time-controls">
          <button
            className={`btn-toggle ${isRunning ? 'btn-stop' : 'btn-start'}`}
            onClick={isRunning ? onStop : onStart}
            title={isRunning ? t('compact.button_stop') : t('compact.button_start')}
          >
            {isRunning ? '⏸' : '▶'}
          </button>
          {false && measurements.length > 0 && !isRunning && (
            <button
              className="btn-toggle btn-reset"
              onClick={onReset}
              title={t('compact.button_reset')}
            >
              ↻
            </button>
          )}
        </div>
      </div>

      {/* Drei Listen nebeneinander (nur wenn Ablauf-DB anzeigen = An, durchgängig mit Normal-Ansicht) */}
      {showAblaufDb && ablaufDbData && ablaufDbData.length > 0 && (
        <div className="compact-lists-container" style={{ height: `${ablaufContainerHeight}px` }}>
          {/* Ablauf-Liste */}
          <div className="compact-list compact-ablauf-list">
            <div className="compact-list-header">{t('compact.ablauf')}</div>
            <div className="compact-list-content">
              {ablaufDbData.map((item, idx) => (
                <button
                  key={idx}
                  className={`compact-list-item ${
                    selectedKuerzel?.kuerzel === item.kuerzel ? 'selected' : ''
                  }`}
                  onClick={() => handleKuerzelClick(item)}
                  title={`${item.kuerzel}: ${item.beschreibung || item.fullText || ''}`}
                >
                  {item.kuerzel}
                </button>
              ))}
            </div>
          </div>

          {/* Einflussgrößen-Liste */}
          <div className="compact-list compact-einflussgroessen-list">
            <div 
              className="compact-list-header"
              onClick={() => {
                // Deselektiere Einflussgröße beim Klick auf Header
                setSelectedEinflussgroesse(null);
              }}
              style={{ cursor: 'pointer' }}
              title={t('compact.click_deselect_title')}
            >
              {t('compact.einflussgroessen')}
            </div>
            <div className="compact-list-content">
              {selectedKuerzel && selectedKuerzel.einflussgroessen && selectedKuerzel.einflussgroessen.length > 0 ? (
                selectedKuerzel.einflussgroessen.map((einfluss, idx) => {
                  if (!einfluss || !einfluss.einflussname) return null;
                  const einflussname = einfluss.einflussname.trim();
                  if (!einflussname) return null;
                  const isSelected = selectedEinflussgroesse === einflussname;
                  
                  return (
                    <button
                      key={idx}
                      className={`compact-list-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleEinflussgroesseSelect(einflussname)}
                      title={`${einflussname}${einfluss.einheit ? ` (${einfluss.einheit})` : ''}`}
                    >
                      <span className="compact-list-item-label">{einflussname}</span>
                    </button>
                  );
                })
              ) : selectedKuerzel ? (
                <div className="compact-list-empty">{t('compact.no_einflussgroessen')}</div>
              ) : (
                <div className="compact-list-empty">{t('compact.no_kuerzel')}</div>
              )}
            </div>
          </div>

          {/* Ausprägungs-Liste */}
          <div className="compact-list compact-auspraegungen-list">
            <div 
              className="compact-list-header"
              onClick={() => {
                // Wenn eine Einflussgröße selektiert ist, lösche die aktuelle Ausprägung
                if (selectedEinflussgroesse && onEinflussgroesseChange) {
                  onEinflussgroesseChange(selectedEinflussgroesse, '');
                }
              }}
              style={{ cursor: selectedEinflussgroesse ? 'pointer' : 'default' }}
              title={selectedEinflussgroesse ? t('compact.click_clear_auspraegung_title') : ''}
            >
              {t('compact.auspraegungen')}
            </div>
            <div className="compact-list-content">
              {selectedKuerzel ? (
                <>
                  {(() => {
                    // Wenn Einflussgröße selektiert: Zeige nur Ausprägungen dieser Einflussgröße
                    if (selectedEinflussgroesse) {
                      const einfluss = selectedKuerzel.einflussgroessen?.find(
                        eg => eg.einflussname?.trim() === selectedEinflussgroesse
                      );
                      
                      if (!einfluss) {
                        return <div className="compact-list-empty">{t('compact.einfluss_not_found')}</div>;
                      }
                      
                      const messwerte = getEinflussOptionValues(einfluss);
                      const hasAuspraegungen = messwerte.length > 0;
                      const currentValue = einflussgroessenValues?.[selectedEinflussgroesse] || '';
                      
                      // Wenn keine Ausprägungen vorhanden: Eingabefeld + Ziffernblock
                      if (!hasAuspraegungen) {
                        return (
                          <div className="compact-auspraegung-input-wrapper">
                            <input
                              ref={auspraegungInputRef}
                              type="text"
                              inputMode="numeric"
                              className="compact-auspraegung-input"
                              placeholder={`${selectedEinflussgroesse} eingeben...`}
                              value={currentValue}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                onEinflussgroesseChange(selectedEinflussgroesse, newValue);
                              }}
                              onBlur={() => {
                                if (ziffernblockClickRef.current) {
                                  ziffernblockClickRef.current = false;
                                  setTimeout(() => {
                                    auspraegungInputRef.current?.focus();
                                  }, 0);
                                }
                              }}
                            />
                            
                            {/* Ziffernblock - immer anzeigen wenn keine Ausprägungen vorhanden */}
                            {!hasAuspraegungen && (
                              <div className="compact-ziffernblock">
                                <div className="ziffernblock-grid">
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                                    <button
                                      key={num}
                                      type="button"
                                      className="ziffernblock-btn"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        ziffernblockClickRef.current = true;
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const currentValue = einflussgroessenValues?.[selectedEinflussgroesse] || '';
                                        const newValue = currentValue + num.toString();
                                        onEinflussgroesseChange(selectedEinflussgroesse, newValue);
                                        setTimeout(() => {
                                          auspraegungInputRef.current?.focus();
                                          ziffernblockClickRef.current = false;
                                        }, 0);
                                      }}
                                    >
                                      {num}
                                    </button>
                                  ))}
                                  <button
                                    type="button"
                                    className="ziffernblock-btn ziffernblock-zero"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      ziffernblockClickRef.current = true;
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const currentValue = einflussgroessenValues?.[selectedEinflussgroesse] || '';
                                      const newValue = currentValue + '0';
                                      onEinflussgroesseChange(selectedEinflussgroesse, newValue);
                                      setTimeout(() => {
                                        auspraegungInputRef.current?.focus();
                                        ziffernblockClickRef.current = false;
                                      }, 0);
                                    }}
                                  >
                                    0
                                  </button>
                                  <button
                                    type="button"
                                    className="ziffernblock-btn ziffernblock-decimal"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      ziffernblockClickRef.current = true;
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const currentValue = einflussgroessenValues?.[selectedEinflussgroesse] || '';
                                      if (!currentValue.includes(',') && !currentValue.includes('.')) {
                                        const decimalSeparator = navigator.language === 'de-DE' || navigator.language.startsWith('de') ? ',' : '.';
                                        const newValue = currentValue + decimalSeparator;
                                        onEinflussgroesseChange(selectedEinflussgroesse, newValue);
                                      }
                                      setTimeout(() => {
                                        auspraegungInputRef.current?.focus();
                                        ziffernblockClickRef.current = false;
                                      }, 0);
                                    }}
                                  >
                                    {navigator.language === 'de-DE' || navigator.language.startsWith('de') ? ',' : '.'}
                                  </button>
                                  <button
                                    type="button"
                                    className="ziffernblock-btn ziffernblock-back"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      ziffernblockClickRef.current = true;
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const currentValue = einflussgroessenValues?.[selectedEinflussgroesse] || '';
                                      const newValue = currentValue.slice(0, -1);
                                      onEinflussgroesseChange(selectedEinflussgroesse, newValue);
                                      setTimeout(() => {
                                        auspraegungInputRef.current?.focus();
                                        ziffernblockClickRef.current = false;
                                      }, 0);
                                    }}
                                    title={t('compact.backspace_title')}
                                  >
                                    ⌫
                                  </button>
                                  <button
                                    type="button"
                                    className="ziffernblock-btn ziffernblock-clear"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      ziffernblockClickRef.current = true;
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onEinflussgroesseChange(selectedEinflussgroesse, '');
                                      setTimeout(() => {
                                        auspraegungInputRef.current?.focus();
                                        ziffernblockClickRef.current = false;
                                      }, 0);
                                    }}
                                  >
                                    C
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      // Wenn Ausprägungen vorhanden: Zeige Buttons
                      return (
                        <>
                          {messwerte.map((auspraegung, idx) => {
                            const isSelected = currentValue === auspraegung;
                            
                            return (
                              <button
                                key={idx}
                                className={`compact-list-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  onEinflussgroesseChange(selectedEinflussgroesse, auspraegung);
                                }}
                                title={auspraegung}
                              >
                                {auspraegung}
                              </button>
                            );
                          })}
                        </>
                      );
                    }
                    
                    // Wenn keine Einflussgröße selektiert: Zeige zuletzt gewählte Ausprägung jeder Einflussgröße
                    if (selectedKuerzel.einflussgroessen && selectedKuerzel.einflussgroessen.length > 0) {
                      const auspraegungItems = selectedKuerzel.einflussgroessen.map((einfluss, idx) => {
                        if (!einfluss || !einfluss.einflussname) return null;
                        const einflussname = einfluss.einflussname.trim();
                        if (!einflussname) return null;
                        
                        const currentValue = einflussgroessenValues?.[einflussname] || '';
                        const isEmpty = !currentValue || currentValue.trim() === '';
                        
                        // Zeige immer einen Eintrag, auch wenn kein Wert vorhanden
                        return (
                          <button
                            key={idx}
                            className={`compact-auspraegung-item ${isEmpty ? 'empty' : ''}`}
                            onClick={() => {
                              // Selektiere die entsprechende Einflussgröße
                              setSelectedEinflussgroesse(einflussname);
                            }}
                            title={`Klicken um ${einflussname} zu bearbeiten`}
                          >
                            <span className="compact-auspraegung-value">
                              {isEmpty ? '\u00A0' : currentValue}
                            </span>
                          </button>
                        );
                      }).filter(Boolean); // Filtert nur die null-Werte heraus
                      
                      if (auspraegungItems.length > 0) {
                        return auspraegungItems;
                      }
                      
                      return <div className="compact-list-empty">{t('compact.no_auspraegungen')}</div>;
                    }
                    
                    return <div className="compact-list-empty">{t('compact.no_einflussgroessen_list')}</div>;
                  })()}
                </>
              ) : (
                <div className="compact-list-empty">{t('compact.no_kuerzel')}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activity und Note Eingabefelder - nur wenn showInputs aktiviert */}
      {showInputs && (
        <div className="compact-input-section">
        <div className="compact-activity-row">
          <input
            type="text"
            className="compact-activity-input"
            placeholder={t('compact.taetigkeit_placeholder')}
            value={activity}
            onChange={(e) => setActivity && setActivity(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && activity.trim() && selectedSession) {
                e.preventDefault();
                // Durchgängig wie Normal-Ansicht: „wie“+Zahl, Kürzel-Auflösung, Messung ohne Leistungsgrad
                if (onActivityEnter) onActivityEnter();
              }
            }}
            disabled={!selectedSession}
          />
          {timeTypes && timeTypes.length > 0 && setTimeType && (
            <select
              className="compact-time-type-select"
              value={timeType || (timeTypes.length > 0 ? timeTypes[0].name : '')}
              onChange={(e) => setTimeType(e.target.value)}
              disabled={!selectedSession}
            >
              {timeTypes.map((tt) => (
                <option key={tt.name} value={tt.name}>
                  {tt.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <textarea
          className="compact-note-input"
          placeholder={t('compact.notiz_placeholder')}
          value={note}
          onChange={(e) => setNote && setNote(e.target.value)}
          rows="2"
          disabled={!selectedSession}
        />
        </div>
      )}

      {/* Mini-Buttons (vor Leistungsgraden) - nur wenn showInputs aktiviert */}
      {showInputs && (
        <div className="compact-mini-buttons">
        <button
          className={`btn-mini btn-voice ${isListening ? 'listening' : ''}`}
          onMouseDown={onVoiceClick}
          onMouseUp={onVoiceClick}
          onTouchStart={onVoiceClick}
          onTouchEnd={onVoiceClick}
          disabled={!selectedSession || !isSpeechRecognitionSupported}
          title={isSpeechRecognitionSupported 
            ? "Spracheingabe - Gedrückt halten"
            : "Sprachsteuerung wird in diesem Browser nicht unterstützt. Bitte verwenden Sie Chrome oder Edge."}
        >
          🎤
        </button>
        <button
          className="btn-mini btn-note"
          onClick={onNoteClick}
          disabled={!selectedSession}
          title={t('compact.note_add_title')}
        >
          📝
        </button>
        <button
          className={`btn-mini btn-photo ${pendingPhotosCount > 0 ? 'has-photos' : ''}`}
          onClick={onPhotoClick}
          disabled={!selectedSession}
          title={pendingPhotosCount > 0 ? t('compact.photo_add_pending', { count: pendingPhotosCount }) : t('compact.photo_add_short')}
        >
          📷{pendingPhotosCount > 0 && `(${pendingPhotosCount})`}
        </button>
        </div>
      )}

      {/* Unterbrechung + Leistungsgrade (mitLeistungsgrade Aus → nur „übernehmen“ über Restbreite) */}
      <div className="compact-grades-section">
        <div className="compact-grades-container">
          <button
            className={`btn-interruption ${isInterrupted ? 'active' : ''}`}
            onClick={() => onInterruptionToggle(!isInterrupted)}
            disabled={!isRunning}
            title={`${t('compact.interruption_title')}${interruptionMeasurementNumber ? ` (#${interruptionMeasurementNumber})` : ''}`}
          >
            ⏸
          </button>
          {mitLeistungsgrade ? (
            grades.map((grade) => (
              <button
                key={grade}
                className={`grade-btn grade-${grade} grade-compact`}
                onClick={() => handleGradeClickWithHaptic(grade)}
                disabled={!selectedSession}
                title={`Leistungsgrad ${grade}%`}
              >
                {grade}
              </button>
            ))
          ) : (
            <button
              className="grade-btn grade-0 grade-compact grade-overnehmen"
              style={{ flex: 1, minWidth: 0 }}
              onClick={() => handleGradeClickWithHaptic(0)}
              disabled={!selectedSession}
              title={t('compact.overnehmen_title')}
            >
              {t('compact.overnehmen_btn')}
            </button>
          )}
        </div>
      </div>

      {/* Resizer-Handle (nur wenn Ablauf-DB Listen sichtbar) */}
      {showAblaufDb && ablaufDbData && ablaufDbData.length > 0 && (
        <div
          ref={resizerRef}
          className={`compact-resizer ${isResizing ? 'resizing' : ''}`}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
        >
          <div className="compact-resizer-handle"></div>
        </div>
      )}

      {/* Tätigkeiten-Liste mit Virtualisierung */}
      <div 
        className="compact-measurements-section"
      >
        <div className="compact-measurements-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>{t('compact.taetigkeiten_count', { count: measurements.length })}</span>
            {(() => {
              const isDemo = selectedSession?.isDemo;
              // Log entfernt - zu häufig bei jedem Render
              return isDemo ? (
                <span className="demo-badge-small">
                  DEMO {measurements.length}/20
                </span>
              ) : null;
            })()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className={`ea-toggle-btn ${showEinflussgroessen ? 'active' : ''}`}
              onClick={() => setShowEinflussgroessen(!showEinflussgroessen)}
              title={t('compact.ea_toggle_title')}
            >
              EA
            </button>
            <button
              className={`note-toggle-btn ${showNotizen ? 'active' : ''}`}
              onClick={() => setShowNotizen(!showNotizen)}
              title={t('compact.note_toggle_title')}
            >
              Notiz
            </button>
            <div className="compact-measurements-navigation">
            <button
              className="nav-btn nav-first"
              onClick={() => {
                if (parentRef.current) {
                  parentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              title={t('compact.nav_first_title')}
              disabled={!canScrollUp}
            >
              ⏮
            </button>
            <button
              className="nav-btn nav-prev"
              onClick={() => {
                if (parentRef.current) {
                  const currentScroll = parentRef.current.scrollTop;
                  const clientHeight = parentRef.current.clientHeight;
                  const pageHeight = clientHeight * 0.8; // 80% der sichtbaren Höhe
                  const newScroll = Math.max(0, currentScroll - pageHeight);
                  parentRef.current.scrollTo({ top: newScroll, behavior: 'smooth' });
                }
              }}
              title={t('compact.nav_prev_title')}
              disabled={!canScrollUp}
            >
              ⏪
            </button>
            <button
              className="nav-btn nav-next"
              onClick={() => {
                if (parentRef.current) {
                  const currentScroll = parentRef.current.scrollTop;
                  const scrollHeight = parentRef.current.scrollHeight;
                  const clientHeight = parentRef.current.clientHeight;
                  const pageHeight = clientHeight * 0.8; // 80% der sichtbaren Höhe
                  const maxScroll = scrollHeight - clientHeight;
                  const newScroll = Math.min(maxScroll, currentScroll + pageHeight);
                  parentRef.current.scrollTo({ top: newScroll, behavior: 'smooth' });
                }
              }}
              title={t('compact.nav_next_title')}
              disabled={!canScrollDown}
            >
              ⏩
            </button>
            <button
              className="nav-btn nav-last"
              onClick={() => {
                if (parentRef.current) {
                  const scrollHeight = parentRef.current.scrollHeight;
                  const clientHeight = parentRef.current.clientHeight;
                  parentRef.current.scrollTo({ top: scrollHeight - clientHeight, behavior: 'smooth' });
                }
              }}
              title={t('compact.nav_last_title')}
              disabled={!canScrollDown}
            >
              ⏭
            </button>
            </div>
          </div>
        </div>
        <div 
          ref={parentRef}
          className="compact-measurements-list"
        >
          <div
            ref={measurementsListRef}
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative'
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const measurement = reversedMeasurements[virtualItem.index];
              const isEmpty = isMeasurementEmpty(measurement);
              const photos = measurementPhotos[measurement.number] || [];
              
              // Swipe-Handler für leere Messungen
              const handleTouchStart = (e) => {
                swipeStartX.current[measurement.id] = e.touches[0].clientX;
              };
              const handleTouchMove = (e) => {
                const startX = swipeStartX.current[measurement.id];
                if (startX === undefined) return;
                const touch = e.touches[0];
                const diff = touch.clientX - startX;
                if (diff < -50) {
                  // Swipe nach links erkannt - öffne Delete-Dialog (wie in Normalansicht)
                  if (onDeleteMeasurement) {
                    onDeleteMeasurement(measurement);
                  }
                  delete swipeStartX.current[measurement.id];
                }
              };
              const handleTouchEnd = (e) => {
                const startX = swipeStartX.current[measurement.id];
                if (startX !== undefined) {
                  const touch = e.changedTouches[0];
                  const diff = touch.clientX - startX;
                  // Wenn kein Swipe erkannt wurde (Bewegung < 10px), löschen wir den Swipe-Start
                  // damit onClick funktionieren kann
                  if (Math.abs(diff) < 10) {
                    delete swipeStartX.current[measurement.id];
                  } else {
                    // Swipe wurde erkannt, aber löschen wir nach kurzer Zeit
                    setTimeout(() => {
                      delete swipeStartX.current[measurement.id];
                    }, 100);
                  }
                }
              };
              
              const swipeHandlers = isEmpty ? {
                onTouchStart: handleTouchStart,
                onTouchMove: handleTouchMove,
                onTouchEnd: handleTouchEnd
              } : {};

              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`
                  }}
                >
                  <div 
                    className={`compact-measurement-item ${isEmpty ? 'swipeable' : ''} ${measurement.excluded ? 'excluded' : ''}`}
                    {...swipeHandlers}
                    onClick={(e) => {
                      // Nur wenn kein Swipe erkannt wurde
                      if (swipeStartX.current[measurement.id] === undefined) {
                        handleMeasurementClick(measurement, e);
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      clearTimeout(clickTimeoutRef.current);
                      setEditingMeasurement(measurement);
                    }}
                  >
                    <div className="compact-measurement-main">
                      {/* Nummer */}
                      <span className="measurement-number">{reversedMeasurements.length - virtualItem.index}.</span>
                      
                      {/* Tätigkeit */}
                      <span className="measurement-activity">{measurement.activity || t('compact.keine_taetigkeit')}</span>
                      
                      {/* Ausgeklammert Badge */}
                      {measurement.excluded && <span className="measurement-excluded-badge">{t('compact.excluded_badge')}</span>}
                      
                      {/* EAs (Einflussgrößen-Ausprägungen) - vor den Zeiten */}
                      {showEinflussgroessen && measurement.einflussgroessen && Object.keys(measurement.einflussgroessen).length > 0 && (
                        <div className="measurement-einflussgroessen-compact">
                          {Object.entries(measurement.einflussgroessen).map(([name, value]) => (
                            <span key={name} className="einflussgroesse-badge-compact">
                              {name}: {value}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Rechtsbündig: FZ, EZ, Leistungsgrad (von rechts nach links) - nur Werte ohne Labels */}
                      <div className="measurement-times-right" style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {measurement.grade && (
                          <span className="measurement-grade">{measurement.grade}</span>
                        )}
                        <span className="measurement-time-ez">
                          {formatTime(measurement.individualTime || 0)}
                        </span>
                        <span className="measurement-time-fz">
                          {formatTime(measurement.progressTime || 0)}
                        </span>
                      </div>
                      {/* Edit-Button und Actions in der ersten Zeile */}
                      <div className="compact-measurement-actions">
                      {onEditMeasurement && (
                        <button
                          onClick={() => setEditingMeasurement(measurement)}
                          className="action-btn action-edit"
                          title={t('compact.edit_title')}
                        >
                          ✏️
                        </button>
                      )}
                      {onViewPhotos && photos.length > 0 && (
                        <button
                          onClick={() => {
                            // Setze Foto-Liste und öffne Lightbox
                            setPhotoPreviewList(photos);
                            setPhotoPreviewIndex(0);
                            setPhotoPreview(photos[0]?.fileData || null);
                          }}
                          className="action-btn action-photos"
                          title={t('compact.photos_title')}
                        >
                          📷 ({photos.length})
                        </button>
                      )}
                      </div>
                    </div>
                    {/* Notiz als 2. Zeile - nur wenn showNotizen aktiviert und Notiz vorhanden */}
                    {showNotizen && measurement.note && measurement.note.trim() && (
                      <div className="measurement-note-compact">
                        {measurement.note}
                      </div>
                    )}
                    {isEmpty && (
                      <div className="swipe-hint">{t('compact.swipe_hint')}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {measurements.length === 0 && (
            <div className="compact-measurements-empty">
              {t('compact.no_taetigkeiten_yet')}
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      {editingMeasurement && (
        <CompactEditDialog
          measurement={editingMeasurement}
          ablaufDbData={ablaufDbData}
          einflussDbData={einflussDbData}
          dependentListsData={dependentListsData}
          timeTypes={timeTypes}
          grades={grades}
          onSave={(updatedMeasurement) => {
            if (onEditMeasurement) {
              onEditMeasurement(updatedMeasurement);
            }
            setEditingMeasurement(null);
          }}
          onCancel={() => setEditingMeasurement(null)}
          onDelete={(measurement) => {
            if (onDeleteMeasurement && measurement) {
              // Übergebe das measurement-Objekt (Dialog wird in App.jsx geöffnet)
              onDeleteMeasurement(measurement);
            }
            setEditingMeasurement(null);
          }}
          formatTime={formatTime}
          darkMode={darkMode}
          selectedSession={selectedSession}
          onPhotoUpload={onPhotoUpload}
          onPhotoDelete={onPhotoDelete}
          measurementPhotos={measurementPhotos}
          loadPhotos={loadPhotos}
        />
      )}

      {/* Foto-Lightbox */}
      {photoPreview && photoPreviewList.length > 0 && (
        <div className="photo-lightbox" onClick={() => {
          setPhotoPreview(null);
          setPhotoPreviewList([]);
          setPhotoPreviewIndex(0);
        }}>
          <div className="photo-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="photo-lightbox-close" 
              onClick={() => {
                setPhotoPreview(null);
                setPhotoPreviewList([]);
                setPhotoPreviewIndex(0);
              }}
            >
              ×
            </button>
            {photoPreviewList.length > 1 && (
              <>
                <button
                  className="photo-lightbox-nav photo-lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = photoPreviewIndex > 0 ? photoPreviewIndex - 1 : photoPreviewList.length - 1;
                    setPhotoPreviewIndex(newIndex);
                    setPhotoPreview(photoPreviewList[newIndex].fileData);
                  }}
                  title={t('compact.photo_prev_title')}
                >
                  ‹
                </button>
                <button
                  className="photo-lightbox-nav photo-lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = photoPreviewIndex < photoPreviewList.length - 1 ? photoPreviewIndex + 1 : 0;
                    setPhotoPreviewIndex(newIndex);
                    setPhotoPreview(photoPreviewList[newIndex].fileData);
                  }}
                  title={t('compact.photo_next_title')}
                >
                  ›
                </button>
                <div className="photo-lightbox-counter">
                  {photoPreviewIndex + 1} / {photoPreviewList.length}
                </div>
              </>
            )}
            <img src={photoPreview} alt={t('compact.photo_alt')} />
          </div>
        </div>
      )}

      {/* Auswertungs-Dialog */}
      {showAnalysis && licenseStatus && getAnalysis && (
        <CompactAnalysisDialog
          analysis={getAnalysis()}
          formatTime={formatTime}
          formatSollzeit={formatSollzeit}
          decimalPlacesSollzeiten={decimalPlacesSollzeiten}
          onClose={onCloseAnalysis}
          darkMode={darkMode}
          pivotDrillDown={pivotDrillDown}
          setPivotDrillDown={setPivotDrillDown}
          timeTypes={timeTypes}
          ablaufDbData={ablaufDbData}
          measurementPhotos={measurementPhotos}
          onPhotoPreview={(fileData) => setPhotoPreview(fileData)}
        />
      )}
    </div>
  );
};

export default CompactPortraitView;
